import React, { useEffect, useMemo, useState } from 'react';
import { MultiSubmissionConfig, MultiSubmissionFieldDefinition } from './types';
import './MultiSubmissionModal.css';

type MultiFieldInputValue = string | boolean;

export interface MultiSubmissionSubmitPayload {
  manifest: Record<string, unknown>;
  files: File[];
}

interface MultiSubmissionModalProps {
  isOpen: boolean;
  competitionId: number;
  config: MultiSubmissionConfig;
  isSubmitting?: boolean;
  /** Read-only preview: the form shows, the final button is disabled and onSubmit never runs. */
  previewOnly?: boolean;
  onClose: () => void;
  onSubmit: (payload: MultiSubmissionSubmitPayload) => Promise<void>;
}

function isValidHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function countWords(value: string): number {
  const trimmed = (value || '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

const MultiSubmissionModal: React.FC<MultiSubmissionModalProps> = ({
  isOpen,
  competitionId,
  config,
  isSubmitting = false,
  previewOnly = false,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<Record<string, MultiFieldInputValue>>({});
  const [filesByField, setFilesByField] = useState<Record<string, File[]>>({});
  const [error, setError] = useState('');

  const initialValues = useMemo(() => {
    const nextValues: Record<string, MultiFieldInputValue> = {};
    for (const field of config.fields) {
      nextValues[field.id] = field.type === 'checkbox' ? false : '';
    }
    return nextValues;
  }, [config.fields]);

  useEffect(() => {
    if (!isOpen) return;
    setValues(initialValues);
    setFilesByField({});
    setError('');
  }, [initialValues, isOpen]);

  if (!isOpen) return null;

  const fieldStringValue = (fieldId: string): string => {
    const value = values[fieldId];
    return typeof value === 'string' ? value : '';
  };

  const fieldBooleanValue = (fieldId: string): boolean => Boolean(values[fieldId]);
  const isWaived = (field: MultiSubmissionFieldDefinition): boolean =>
    Boolean(field.waivedByCheckbox && fieldBooleanValue(field.waivedByCheckbox));
  const overWordLimit = (field: MultiSubmissionFieldDefinition): boolean =>
    typeof field.maxWords === 'number' && countWords(fieldStringValue(field.id)) > field.maxWords;
  const hasWordLimitError = config.fields.some(overWordLimit);

  const handleFileChange = (field: MultiSubmissionFieldDefinition, fileList: FileList | null) => {
    const selected = fileList ? Array.from(fileList) : [];
    setFilesByField((prev) => ({
      ...prev,
      [field.id]: field.multiple ? selected : selected.slice(0, 1),
    }));
    setError('');
  };

  const validate = (): string | null => {
    for (const field of config.fields) {
      if (isWaived(field)) continue;
      if (field.type === 'file') {
        const files = filesByField[field.id] || [];
        if (field.required && files.length === 0) {
          return `${field.label} is required.`;
        }
        if (field.accept) {
          const acceptedTypes = field.accept.toLowerCase().split(',').map((entry) => entry.trim());
          const acceptsFile = (file: File) => acceptedTypes.some((entry) =>
            entry.startsWith('.')
              ? file.name.toLowerCase().endsWith(entry)
              : entry.endsWith('/*')
                ? file.type.toLowerCase().startsWith(entry.slice(0, -1))
                : file.type.toLowerCase() === entry,
          );
          if (files.some((file) => !acceptsFile(file))) {
            return `${field.label} must use an accepted file format (${field.accept}).`;
          }
        }
        continue;
      }

      if (field.type === 'checkbox') {
        const checked = fieldBooleanValue(field.id);
        if (field.required && !checked) {
          return `Please confirm: ${field.label}`;
        }
        continue;
      }

      const value = fieldStringValue(field.id).trim();
      if (field.required && !value) {
        return `${field.label} is required.`;
      }
      if (field.type === 'url' && value && !isValidHttpUrl(value)) {
        return `${field.label} must be a valid http(s) URL.`;
      }
      if (typeof field.maxWords === 'number' && field.maxWords > 0) {
        const wordCount = countWords(value);
        if (wordCount > field.maxWords) {
          return `${field.label} must be ${field.maxWords} words or fewer.`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting || previewOnly) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const files: File[] = [];
    const fieldsPayload = config.fields.map((field) => {
      if (field.type === 'file') {
        const selectedFiles = isWaived(field) ? [] : filesByField[field.id] || [];
        selectedFiles.forEach((file) => files.push(file));
        return {
          id: field.id,
          label: field.label,
          type: field.type,
          required: Boolean(field.required),
          value: selectedFiles.map((file) => ({
            name: file.name,
            size_bytes: file.size,
            mime_type: file.type || 'application/octet-stream',
          })),
        };
      }

      if (field.type === 'checkbox') {
        return {
          id: field.id,
          label: field.label,
          type: field.type,
          required: Boolean(field.required),
          value: fieldBooleanValue(field.id),
        };
      }

      return {
        id: field.id,
        label: field.label,
        type: field.type,
        required: Boolean(field.required),
        value: fieldStringValue(field.id).trim(),
      };
    });

    const valuesPayload: Record<string, unknown> = {};
    for (const field of fieldsPayload) {
      valuesPayload[field.id] = field.value;
    }

    const manifest: Record<string, unknown> = {
      schema: config.key,
      competition_id: competitionId,
      generated_at: new Date().toISOString(),
      fields: fieldsPayload,
      values: valuesPayload,
    };

    try {
      setError('');
      await onSubmit({ manifest, files });
      onClose();
    } catch {
      // Parent sets and shows API error.
    }
  };

  return (
    <div className="multi-submission-modal-backdrop" onClick={onClose}>
      <div
        className="multi-submission-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="multi-submission-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="multi-submission-modal__header">
          <h3 id="multi-submission-modal-title">{config.title}</h3>
          <button
            type="button"
            className="multi-submission-modal__close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {config.description && <p className="multi-submission-modal__description">{config.description}</p>}

        <form className="multi-submission-modal__form" onSubmit={handleSubmit}>
          {config.fields.filter((field) => !isWaived(field)).map((field) => (
            <label key={field.id} className={`multi-submission-modal__field${field.type === 'checkbox' ? ' multi-submission-modal__field--checkbox' : ''}`}>
              <span className="multi-submission-modal__label">
                {field.label}
                {field.required ? ' *' : ''}
              </span>

              {field.type === 'textarea' ? (
                <textarea
                  value={fieldStringValue(field.id)}
                  aria-invalid={overWordLimit(field)}
                  aria-describedby={field.maxWords ? `${field.id}-word-count` : undefined}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [field.id]: e.target.value,
                    }))
                  }
                  placeholder={field.placeholder}
                  rows={4}
                />
              ) : field.type === 'select' ? (
                <select
                  value={fieldStringValue(field.id)}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [field.id]: e.target.value,
                    }))
                  }
                >
                  <option value="">Select an option</option>
                  {(field.options || []).map((option) => (
                    <option key={`${field.id}-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'checkbox' ? (
                <input
                  type="checkbox"
                  checked={fieldBooleanValue(field.id)}
                  onChange={(e) => {
                    setValues((prev) => ({
                      ...prev,
                      [field.id]: e.target.checked,
                    }));
                    if (e.target.checked) {
                      setFilesByField((prev) => {
                        const next = { ...prev };
                        config.fields.forEach((dependent) => {
                          if (dependent.waivedByCheckbox === field.id) delete next[dependent.id];
                        });
                        return next;
                      });
                    }
                    setError('');
                  }}
                />
              ) : field.type === 'file' ? (
                <>
                  <input
                    type="file"
                    accept={field.accept}
                    multiple={Boolean(field.multiple)}
                    onChange={(e) => handleFileChange(field, e.target.files)}
                  />
                  {(filesByField[field.id] || []).length > 0 && (
                    <ul className="multi-submission-modal__files">
                      {(filesByField[field.id] || []).map((f) => (
                        <li key={`${field.id}-${f.name}-${f.lastModified}`}>{f.name}</li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <input
                  type={field.type === 'url' ? 'url' : 'text'}
                  value={fieldStringValue(field.id)}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [field.id]: e.target.value,
                    }))
                  }
                  placeholder={field.placeholder}
                />
              )}

              {field.helpText && <span className="multi-submission-modal__help">{field.helpText}</span>}
              {typeof field.maxWords === 'number' && field.maxWords > 0 && (
                <span id={`${field.id}-word-count`} className={`multi-submission-modal__word-count${overWordLimit(field) ? ' multi-submission-modal__word-count--error' : ''}`} aria-live="polite">
                  {countWords(fieldStringValue(field.id))}/{field.maxWords} words
                  {overWordLimit(field) && ' — Shorten your description to continue.'}
                </span>
              )}
            </label>
          ))}

          {error && <div className="multi-submission-modal__error">{error}</div>}

          <div className="multi-submission-modal__actions">
            <button type="button" className="multi-submission-modal__btn multi-submission-modal__btn--secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="multi-submission-modal__btn multi-submission-modal__btn--primary" disabled={previewOnly || isSubmitting || hasWordLimitError}>
              {previewOnly ? 'Preview only' : isSubmitting ? 'Submitting...' : config.submitLabel || 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MultiSubmissionModal;
